import React, { useMemo } from 'react';
import { StyleSheet, TouchableHighlight, TouchableNativeFeedback, Image, Text, View, Platform } from 'react-native';
import type { TextStyle } from 'react-native';
import RemoteImage from 'src/presentation/components/molecules/RemoteImage';
import { useMapStore } from 'src/domain/stores/useMapStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Icons from 'src/core/utils/Icons';
import Colors from 'src/core/utils/Colors';
import type { ApiPage } from 'src/core/models/api/ApiPage';

interface ImageMapToasterProps {
	toastUrl: ApiPage | null;
	onLocationDetailClick: (toastUrl: ApiPage) => void;
}

export default function ImageMapToaster({ toastUrl, onLocationDetailClick }: ImageMapToasterProps) {
	const markerData = useMapStore((state) => state.markerData) as {
		image?: string;
		title?: string;
		description?: string;
	} | null;
	const navigate = useNavigationStore((state) => state.navigate);
	const contentWidth = useContentWidth();

	// The toaster spans the content width, so that is what its text has to fit in.
	const sizedStyles = useMemo(
		(): { toastTitle: TextStyle; toastDesc: TextStyle } => ({
			toastTitle: {
				fontFamily: Fonts.montserratBold,
				color: Colors.greyHeader,
				fontSize: contentWidth < 321 ? Dimens.fontSize14 : Dimens.fontSize17,
				marginBottom: Dimens.padding4,
			},
			toastDesc: {
				fontFamily: Fonts.montserratRegular,
				color: Colors.greyHeader,
				fontSize: contentWidth < 321 ? Dimens.fontSize12 : Dimens.fontSize14,
				lineHeight: 19,
			},
		}),
		[contentWidth],
	);

	const toPage = () => {
		if (!toastUrl) return;
		navigate(toastUrl.type, { data: toastUrl });
		onLocationDetailClick(toastUrl);
	};

	// The whole row is one tappable target so a tap anywhere opens the linked page.
	const row = (
		<View style={styles.toastWrap}>
			{!!markerData?.image && (
				<RemoteImage key={markerData.image} resizeMode="cover" style={styles.markerImage} uri={markerData.image} />
			)}
			<View style={styles.toastContent}>
				<Text numberOfLines={1} style={sizedStyles.toastTitle}>
					{markerData?.title}
				</Text>
				{!!markerData?.description && (
					<Text numberOfLines={1} style={sizedStyles.toastDesc}>
						{markerData.description}
					</Text>
				)}
			</View>
			{toastUrl !== null && (
				<View style={styles.toastIcon}>
					<Image source={Icons.mapInfoIndicator} style={styles.indicator} />
				</View>
			)}
		</View>
	);

	if (Platform.OS === 'android') {
		return <TouchableNativeFeedback onPress={toPage}>{row}</TouchableNativeFeedback>;
	}

	return (
		<TouchableHighlight style={styles.touchable} underlayColor={Colors.greyBgLight} onPress={toPage}>
			{row}
		</TouchableHighlight>
	);
}

const styles = StyleSheet.create({
	touchable: {
		flex: 1,
	},
	toastWrap: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: Dimens.padding12,
	},
	markerImage: {
		width: 56,
		height: 56,
		borderRadius: 8,
		marginLeft: Dimens.margin16,
	},
	indicator: {
		width: 8,
		height: 13,
	},
	toastContent: {
		flex: 1,
		paddingHorizontal: Dimens.padding16,
	},
	toastIcon: {
		justifyContent: 'center',
		alignItems: 'center',
		width: 50,
	},
});
